<template>
  <section ref="emblaRef" class="relative overflow-hidden">
    <div
      class="-ml-5 grid auto-cols-[calc(2/3*100%)] grid-flow-col min-[340px]:auto-cols-[50%] min-[540px]:auto-cols-[40%] min-[800px]:auto-cols-[calc(2/7*100%)] min-[1020px]:auto-cols-[calc(2/9*100%)] min-[1460px]:auto-cols-[calc(2/11*100%)] min-[1710px]:auto-cols-[calc(2/13*100%)] min-[1900px]:auto-cols-[calc(5/37*100%)]"
      style="backface-visibility: hidden; touch-action: pan-y pinch-zoom"
    >
      <div v-for="(item, index) of props.items" :key="index" class="pl-5">
        <TiedProductsCard :product="item" :index="index" :list="props.list" @after-product-added="emit('productAdded')" />
      </div>
    </div>

    <div v-if="props.items.length > 1" class="pointer-events-none absolute inset-0 z-0 flex items-center justify-between overflow-hidden">
      <div class="flex h-full w-16 items-center bg-gradient-to-r from-background from-25% via-background/75 via-60%" :class="{ invisible: isFirstSlide }">
        <button
          class="flex size-12 items-center justify-center rounded-full border border-border p-2 transition-all hover:border-border-hover focus:border-border-hover active:border-border-active"
          type="button"
          :class="isFirstSlide ? 'pointer-events-none' : 'pointer-events-auto'"
          :aria-label="t['global.previous']()"
          @click="() => emblaApi?.scrollPrev()"
        >
          <Icon class="size-5" name="arrow-left" />
        </button>
      </div>
      <div class="flex h-full w-16 items-center bg-gradient-to-l from-background from-25% via-background/75 via-60%" :class="{ invisible: isLastSlide }">
        <button
          class="flex size-12 items-center justify-center rounded-full border border-border p-2 transition-all hover:border-border-hover focus:border-border-hover active:border-border-active"
          type="button"
          :class="isLastSlide ? 'pointer-events-none' : 'pointer-events-auto'"
          :aria-label="t['global.next']()"
          @click="emblaApi?.scrollNext()"
        >
          <Icon class="size-5" name="arrow-right" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import TiedProductsCard from '~/components/product/TiedProductsCard.vue'
import { useResizeObserver } from '@vueuse/core'
import emblaCarouselVue from 'embla-carousel-vue'
import * as t from '~/messages'
import type { TiedProductsCardProduct } from './tied-products'

const props = defineProps<{
  items: TiedProductsCardProduct[]
  list: string
}>()

const [emblaRef, emblaApi] = emblaCarouselVue({
  align: 'center',
  slidesToScroll: 1,
  breakpoints: {
    '(min-width: 340px)': {
      align: 'start',
      slidesToScroll: 2,
    },
    '(min-width: 800px)': {
      align: 'start',
      slidesToScroll: 3,
    },
    '(min-width: 1020px)': {
      align: 'start',
      slidesToScroll: 4,
    },
    '(min-width: 1460px)': {
      align: 'start',
      slidesToScroll: 5,
    },
    '(min-width: 1710px)': {
      align: 'start',
      slidesToScroll: 6,
    },
    '(min-width: 1900px)': {
      align: 'start',
      slidesToScroll: 7,
    },
  },
})

const initAlreadySeen = () => props.items.map((_) => false)
const alreadySeen = ref(initAlreadySeen())
watch(
  () => props.items,
  () => {
    alreadySeen.value = initAlreadySeen()
  }
)

const { proxy: gtmProxy } = useGtm()

const isScrollable = ref(true)

const toggleActiveWhenScrollable = () => {
  isScrollable.value = (emblaApi.value?.internalEngine().scrollSnaps.length ?? 0) > 1
}

watch(isScrollable, () => {
  emblaApi.value?.reInit({ active: isScrollable.value })
})

const isFirstSlide = ref(true)
const isLastSlide = ref(true)

onMounted(() => {
  const updateSelectedIndex = () => {
    isFirstSlide.value = !emblaApi.value?.canScrollPrev()
    isLastSlide.value = !emblaApi.value?.canScrollNext()
  }

  if (emblaApi.value) {
    emblaApi.value.on('init', updateSelectedIndex).on('reInit', updateSelectedIndex).on('select', updateSelectedIndex).on('slidesInView', updateSlidesInView)

    toggleActiveWhenScrollable()
  }
})

useResizeObserver(
  emblaRef,
  useThrottleFrame(() => {
    toggleActiveWhenScrollable()
  })
)

const updateSlidesInView = () => {
  const indices = emblaApi.value?.slidesInView()

  if (!indices) {
    return
  }

  const newSlides = indices.filter((id) => !alreadySeen.value[id])
  newSlides.forEach((id) => (alreadySeen.value[id] = true))

  sendGtmImpression(newSlides)
}

const sendGtmImpression = (indices: number[]) => {
  if (!props.items) {
    return
  }

  const chunkSize = 40
  for (let i = 0; i < indices.length; i += chunkSize) {
    const chunk = indices.slice(i, i + chunkSize)

    gtmProxy.dataLayer.push({
      event: 'impression',
      ecommerce: {
        impressions: chunk.map((index) => {
          const p = props.items[index]!

          return {
            name: p.name,
            id: p.id,
            list: props.list,
            position: index + 1,
          }
        }),
      },
    })
  }
}

const emit = defineEmits<{
  productAdded: []
}>()
</script>
